import React from 'react';
import { useIntl } from 'react-intl';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { ButtonVariant } from '~/eds/src/types/ButtonVariant';
import { BulkActionType } from '~/enums/BulkActionType';
import { actionTypeContent, actionTypeTitle } from '~/utils/bulkActionLimits';

type Callback = () => void;

interface Props {
  limit: number;
  count: number | undefined;
  actionType: BulkActionType;
  primaryActionHide: Callback;
}

export const useLimitExceededModal = ({
  limit,
  count = 0,
  actionType,
  primaryActionHide,
}: Props): [React.ReactNode, Callback, Callback] => {
  const intl = useIntl();

  const children = (
    <Layout spacing={3}>
      <Icon icon="status-warning" color="status.warning" size="l" />
      <Text>
        {intl.formatMessage(
          {
            id: 'UseLimitExceededModal.limitMessage',
            defaultMessage:
              'Only {limit} documents can be {action} at a time. Please select fewer documents to complete this action.',
            description:
              'Message shown when bulk action limit is exceeded. Example: limit = "100", action = "deleted".',
          },
          {
            limit,
            action: actionTypeContent(actionType, intl),
          },
        )}
      </Text>
    </Layout>
  );

  const props = {
    children,
    primaryAction: {
      text: intl.formatMessage({
        id: 'UseLimitExceededModal.gotItButton',
        defaultMessage: 'Got it',
        description: 'Button text to acknowledge limit exceeded message',
      }),
      variant: 'primary' as ButtonVariant,
      onClick: primaryActionHide,
    },
    onCancel: null,
    title: intl.formatMessage(
      {
        id: 'UseLimitExceededModal.title',
        defaultMessage: '{actionTitle} {count} Documents: Limit Exceeded',
        description:
          'Title for limit exceeded modal. Example: actionTitle = "Restoring", count = "500".',
      },
      {
        actionTitle: actionTypeTitle(actionType, intl),
        count,
      },
    ),
  };

  return useModal(props);
};
